import {
  type ApolloClient,
  type ApolloError,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { usePage } from 'src/components/Router/usePage';
import type { UnconfirmedActionName } from 'src/modules/analytics/types';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { ActionName } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useQuery, useToastMutation } from '../../../modules/apollo';
import { CREATE_RESUME_PAGE } from '../../Router';
import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  addUnconfirmedRole,
  addUnconfirmedRoleFailure,
  addUnconfirmedRoleSuccess,
  deleteRole,
  deleteRoleFailure,
  deleteRoleSuccess,
  rejectUnconfirmedRole,
  rejectUnconfirmedRoleFailure,
  rejectUnconfirmedRoleSuccess,
  updateCareerHistory,
  updateCareerHistoryFailure,
  updateCareerHistorySuccess,
} from './actionCreators';
import {
  generateConfirmRoleOptimisticResponse,
  generateDeleteRoleOptimisticResponse,
  generateRejectRoleOptimisticResponse,
  generateUpdateRoleOptimisticResponse,
} from './apolloCache.optimistic';
import {
  updateCacheConfirmRoleMutation,
  updateCacheDeleteRole,
  updateCacheRejectRoleMutation,
  updateCacheUpdateRole,
} from './apolloCache.updates';

export const useGetConfirmedRoles = () => {
  const { data, loading, error } = useQuery(Schema.GetConfirmedRolesDocument);

  const confirmedRoles = data?.viewer?.confirmedRoles ?? [];

  return {
    confirmedRoles,
    loading,
    error,
  };
};

export const getConfirmedRolesFromCache = (
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>,
) => {
  const confirmedRolesData = getDataFromCache({
    cache: apolloClient.cache,
    query: Schema.GetConfirmedRolesDocument,
  });

  return {
    confirmedRoles: confirmedRolesData?.viewer?.confirmedRoles || [],
  };
};

export const useGetUnconfirmedRoles = () => {
  const page = usePage();
  const isCreateResumePage = page === CREATE_RESUME_PAGE;
  const shouldSkip = isCreateResumePage;

  const {
    data: queryData,
    loading,
    error,
  } = useQuery(Schema.GetUnconfirmedRolesDocument, {
    skip: shouldSkip,
    bypassErrorPageOnFail: true,
  });

  const unconfirmedRoles = queryData?.viewer?.unconfirmedRoles ?? [];

  return {
    unconfirmedRoles: isCreateResumePage ? [] : unconfirmedRoles,
    loading,
    error,
  };
};

export const getUnconfirmedRolesFromCache = (
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>,
) => {
  const unconfirmedRolesData = getDataFromCache({
    cache: apolloClient.cache,
    query: Schema.GetUnconfirmedRolesDocument,
  });

  return {
    unconfirmedRoles: unconfirmedRolesData?.viewer?.unconfirmedRoles || [],
  };
};

const key = 'career-history';

export const useUpdateConfirmedRoles = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const { candidateId } = useCandidateId();

  const onCompleted = () => {
    dispatch(updateCareerHistorySuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updateCareerHistoryFailure(error));
  };

  return useToastMutation(Schema.UpdateRoleDocument, {
    key,
    errorActionRouteParameters: {
      card: 'career-history',
      mode: 'READ',
    },
    onMutate: ({ role }) => {
      dispatch(updateCareerHistory(role, action));
    },
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD'
        ? t('Career history added')
        : t('Career history updated'),
    update: updateCacheUpdateRole,
    optimisticResponse: generateUpdateRoleOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }) as any, // NOTE: currently typed as any to avoid typing error due to removal of `function` and `industry` fields. Can be removed once they're removed from graphql schema
  });
};

export const useRemoveConfirmedRole = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = () => {
    dispatch(deleteRole());
  };

  const onCompleted = () => {
    dispatch(deleteRoleSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(deleteRoleFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteRoleDocument, {
    key,
    errorActionRouteParameters: { card: 'career-history', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Career history removed'),
    update: updateCacheDeleteRole,
    optimisticResponse: generateDeleteRoleOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }) as any, // NOTE: currently typed as any to avoid typing error due to removal of `function` and `industry` fields. Can be removed once they're removed from graphql schema
  });
};

export const useAddUnconfirmedRole = (action: UnconfirmedActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = (
    { role }: { role: Omit<Schema.ConfirmRoleInput, 'function' | 'industry'> },
    mutationMetadata?: { contentRead: boolean },
  ) => {
    dispatch(
      addUnconfirmedRole(
        { id: role.id },
        action,
        mutationMetadata?.contentRead,
      ),
    );
  };

  const onCompleted = () => {
    dispatch(addUnconfirmedRoleSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(addUnconfirmedRoleFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.ConfirmRoleDocument, {
    key,
    errorActionRouteParameters: { card: 'career-history', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Career history added'),
    update: updateCacheConfirmRoleMutation,
    optimisticResponse: generateConfirmRoleOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }) as any, // NOTE: currently typed as any to avoid typing error due to removal of `function` and `industry` fields. Can be removed once they're removed from graphql schema
  });
};

export const useRejectUnconfirmedRole = () => {
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = ({ role }: Schema.RejectRoleMutationVariables) => {
    dispatch(rejectUnconfirmedRole(role));
  };

  const onCompleted = () => {
    dispatch(rejectUnconfirmedRoleSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(rejectUnconfirmedRoleFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.RejectRoleDocument, {
    key,
    errorActionRouteParameters: { card: 'career-history', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    update: updateCacheRejectRoleMutation,
    optimisticResponse: generateRejectRoleOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }),
  });
};
